import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { ConditionOutcome } from 'components/powerFlowShared/components/types';
import { flowNodeColor } from 'fable/flowEditor';
import { AutomationExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';
import type { Relation } from 'flow-builder/types/relation';
import { AutomationNodeTypes } from 'hocs';

import { createConditionModalContainer } from '../../../containers/condition';
import { CONDITION_NODE_LABEL } from '../../constants';
import { ConditionModalPropsT } from '../../modals';
import { MatchingRuleT, MatchingStrategy } from '../types';

export type AutomationNodeConditionT = Relation<
  Payload,
  AutomationNodeTypes.CONDITION
>;

export type Payload = {
  matchNodeId: string;
  nonMatchNodeId: string;
  matchingStrategy: MatchingStrategy;
  rules: MatchingRuleT[];
};

type ConditionNodeAttributes = GenericNodeAttributes<
  Payload,
  AutomationExtraData
>;
type ConditionNodeProps = GenericNodeProps<Payload, AutomationExtraData>;

const translatePayload: ConditionNodeAttributes['translatePayload'] = (
  node,
) => {
  const { nonMatchNodeId, matchNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: ConditionOutcome.NO_MATCH,
      connection: nonMatchNodeId || uuid(),
    },
    {
      id: uuid(),
      label: ConditionOutcome.MATCH,
      connection: matchNodeId || uuid(),
    },
  ];
};

const updateConnection: ConditionNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const key =
    connection.connectionLabel === ConditionOutcome.MATCH
      ? 'matchNodeId'
      : 'nonMatchNodeId';
  const payload = { ...node.payload, [key]: connectionId };

  return { ...node, payload };
};

const ConditionModal = createConditionModalContainer<AutomationNodeConditionT>(
  AutomationNodeTypes.CONDITION,
  (node: ConditionModalPropsT<AutomationNodeConditionT>['node']) => {
    if (node.nodeType === 'action') {
      return {
        matchNodeId: uuid(),
        nonMatchNodeId: uuid(),
        matchingStrategy: MatchingStrategy.ALL,
        rules: [],
      } as AutomationNodeConditionT['payload'];
    }

    return node.payload;
  },
);

export const ConditionNodeAttributes: ConditionNodeAttributes = {
  iconName: 'branch',
  label: CONDITION_NODE_LABEL,
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: ConditionNode,
  ActionModal: ConditionModal,
};

export function ConditionNode({ node }: ConditionNodeProps): JSX.Element {
  const { payload } = node;
  const { rules } = payload;

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text
              weight="semibold"
              text={`${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`}
            />
          </TextEllipsis>
        ),
        [rules],
      )}
    </TextContent>
  );
}
